import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { City } from '../types/trip';
import { FlightData, TripData } from '../pages/AccountPage';
import { addDays, differenceInDays } from 'date-fns';
import { nanoid } from 'nanoid';
import { tripApi } from '../utils/api';
import { SelectedAccommodation } from '../types/accommodation';
import { AccommodationService, AccommodationPreferences } from '../services/accommodationService';
import { generateDistinctPastelColor, removeColorForCity, updateCityColorsArray } from '../utils/colorUtils';
import { searchFlights, FlightSearchResponse } from '../services/flightService';
import { useLocalStorage } from '../hooks/useLocalStorage';
import {
  TRIP_STORAGE_KEY,
  CURRENT_TRIP_STORAGE_VERSION,
  getDefaultTripStorage,
  serializeTripData,
  deserializeTripData,
  migrateTripStorage,
  compressTripStorage,
  type TripStorageSchemaV1,
} from '../types/tripStorage';

// Interface for transit data
export interface TransitRouteData {
  polyline?: string | (string | undefined)[] | undefined;
  duration?: string;
  distance?: string;
  routeDetails?: any;
  fromCity?: string;
  toCity?: string;
  price?: number;
  mode?: string;
  departureTime?: string;
  arrivalTime?: string;
  company?: string;
  transitDetails?: Array<{
    type: string;
    provider: string;
    departureTime?: string;
    arrivalTime?: string;
    departureStop?: string;
    arrivalStop?: string;
    polyline?: string;
  }>;
}

export interface CityCostDetails {
  accommodation: number;
  activities: number;
  transport: number;
  total: number;
  accommodationName?: string;
  activityName?: string;
  // Enhanced accommodation details
  roomName?: string;
  boardType?: string;
  rateId?: string;
  accommodationId?: string;
  accommodationAddress?: string;
  accommodationRating?: number;
  isCustomAccommodation?: boolean;
}

export interface DayDetails {
  date: Date;
  accommodation?: string;
  accommodationPrice?: number;
  activities?: string;
  activitiesPrice?: number;
  transport?: string;
  transportPrice?: number;
  // Enhanced accommodation details
  accommodationDetails?: {
    roomName?: string;
    boardType?: string;
    rateId?: string;
    description?: string;
    photos?: string[];
    amenities?: string[];
    id?: string;
    address?: string;
    rating?: number;
  };
}

// Interface for transport connector data
export interface TransportConnectorData {
  mode: string;
  cost: number;
  provider?: string;
  duration?: string;
  departureTime?: string;
  arrivalTime?: string;
  emissions?: number;
}

// Interface for context state
interface TripContextState {
  // Existing properties
  tripData: TripData | null;
  transitRoutes: { [key: string]: TransitRouteData };
  accommodationSearchResults: { [cityName: string]: any[] }; // Store all accommodation search results by city
  transportConnectorData: { [key: number]: TransportConnectorData }; // Store transport connector data by index
  setTripData: (data: TripData | null) => void;
  updateTransitRoute: (fromCity: string, toCity: string, routeData: TransitRouteData) => void;
  getTransitRoute: (fromCity: string, toCity: string) => TransitRouteData | undefined;
  clearTransitRoutes: () => void;
  storeAccommodationSearchResults: (cityName: string, results: any[]) => void;
  getAccommodationSearchResults: (cityName: string) => any[] | undefined;
  updateTransportConnectorData: (index: number, data: TransportConnectorData) => void;
  getTransportConnectorData: (index: number) => TransportConnectorData | undefined;
  clearTransportConnectorData: () => void;
  clearStorageAndStartFresh: () => void;
  
  // New load trip functionality
  loadTrip: (userId: string, tripId: string) => Promise<void>;
  
  // Flight loading state
  isFlightsLoading: boolean;
  setIsFlightsLoading: (loading: boolean) => void;
  hasInitialFlightSearchCompleted: boolean;
  setHasInitialFlightSearchCompleted: (completed: boolean) => void;

  // Accommodation loading state
  isAccommodationsLoading: boolean;
  setIsAccommodationsLoading: (loading: boolean) => void;
  hasInitialAccommodationSearchCompleted: boolean;
  setHasInitialAccommodationSearchCompleted: (completed: boolean) => void;
  
  // New properties for comprehensive trip management
  updateTripDetails: (details: Partial<TripData['tripDetails']>) => void;
  updateSelectedCities: (cities: City[]) => void;
  addCity: (city: City) => void;
  removeCity: (cityName: string) => void;
  updateDaysArray: (daysArray: number[]) => void;
  updateDaysForCity: (index: number, days: number) => void;
  updateCityCosts: (cityCosts: { [key: string]: CityCostDetails }) => void;
  updateCityCost: (cityName: string, costType: keyof CityCostDetails, value: number | string) => void;
  updateCityColors: (cityColors: Array<{ city: string; color: string }>) => void;
  updateCityDayDetails: (cityDayDetails: { [key: string]: DayDetails[] }) => void;
  updateDayDetailsForCity: (cityName: string, dayDetails: DayDetails[]) => void;
  updateCityNotes: (cityId: string, notes: string) => void;
  resetTripContext: () => void;
  setOutboundFlight: (flight: FlightData | undefined) => void;
  setInboundFlight: (flight: FlightData | undefined) => void;
  calculateTotalTripCost: () => number;
  calculateStartDate: (index: number) => Date | undefined;
  reorderCities: (sourceIndex: number, destinationIndex: number) => void;
  storeAccommodation: (cityName: string, accommodation: SelectedAccommodation) => void;
  
  // New flight re-search functionality
  isFlightRefreshing: boolean;
  setIsFlightRefreshing: (refreshing: boolean) => void;
}

// Create context with default values
const TripContext = createContext<TripContextState>({
  tripData: null,
  transitRoutes: {},
  accommodationSearchResults: {},
  transportConnectorData: {},
  setTripData: () => {},
  updateTransitRoute: () => {},
  getTransitRoute: () => undefined,
  clearTransitRoutes: () => {},
  storeAccommodationSearchResults: () => {},
  getAccommodationSearchResults: () => undefined,
  updateTransportConnectorData: () => {},
  getTransportConnectorData: () => undefined,
  clearTransportConnectorData: () => {},
  loadTrip: async () => {}, // Add default implementation
  
  // Flight loading defaults
  isFlightsLoading: false,
  setIsFlightsLoading: () => {},
  hasInitialFlightSearchCompleted: false,
  setHasInitialFlightSearchCompleted: () => {},

  // Accommodation loading defaults
  isAccommodationsLoading: false,
  setIsAccommodationsLoading: () => {},
  hasInitialAccommodationSearchCompleted: false,
  setHasInitialAccommodationSearchCompleted: () => {},
  
  // Defaults for new functions
  updateTripDetails: () => {},
  updateSelectedCities: () => {},
  addCity: () => {},
  removeCity: () => {},
  updateDaysArray: () => {},
  updateDaysForCity: () => {},
  updateCityCosts: () => {},
  updateCityCost: () => {},
  updateCityColors: () => {},
  updateCityDayDetails: () => {},
  updateDayDetailsForCity: () => {},
  updateCityNotes: () => {},
  setOutboundFlight: () => {},
  setInboundFlight: () => {},
  calculateTotalTripCost: () => 0,
  calculateStartDate: () => undefined,
  reorderCities: () => {},
  resetTripContext: () => {},
  storeAccommodation: () => {},
  clearStorageAndStartFresh: () => {},
  
  // New flight re-search defaults
  isFlightRefreshing: false,
  setIsFlightRefreshing: () => {}
});

  // Provider component
export const TripProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Reference to track city coordinate update events
  const cityCoordinateUpdateRef = useRef<boolean>(false);
  
  // Add event listener for city coordinate updates
  useEffect(() => {
    const handleCityCoordinatesUpdate = (event: CustomEvent) => {
      const { cityId, coordinates } = event.detail;
      
      if (!cityId || !coordinates) return;
      
      console.log('🌍 [TripContext] Received city coordinates update:', { cityId, coordinates });
      
      // Update the city coordinates in tripData
      setTripDataState(prev => {
        if (!prev) return prev;
        
        // Find the city by ID and update its coordinates
        const updatedCities = prev.selectedCities.map(city => {
          if (city.id === cityId) {
            console.log('🌍 [TripContext] Updating coordinates for city:', city.name);
            return {
              ...city,
              coordinates: coordinates
            };
          }
          return city;
        });
        
        // Return updated trip data
        const updatedTripData = {
          ...prev,
          selectedCities: updatedCities
        };
        
        // Save to storage
        try {
          localStorage.setItem('unsavedTripData', JSON.stringify(updatedTripData));
          sessionStorage.setItem('tripData', JSON.stringify(updatedTripData));
        } catch (error) {
          console.error('❌ [TripContext] Error saving updated coordinates to storage:', error);
        }
        
        return updatedTripData;
      });
    };
    
    // Add event listener
    window.addEventListener('city-coordinates-update', handleCityCoordinatesUpdate as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('city-coordinates-update', handleCityCoordinatesUpdate as EventListener);
    };
  }, []);
  // Use new versioned localStorage hook for complete trip storage
  const [tripStorage, setTripStorage, removeTripStorage] = useLocalStorage<TripStorageSchemaV1>({
    key: TRIP_STORAGE_KEY,
    defaultValue: getDefaultTripStorage(),
    version: CURRENT_TRIP_STORAGE_VERSION,
    migrate: migrateTripStorage,
    serialize: serializeTripData,
    deserialize: deserializeTripData,
    syncAcrossTabs: true,
  });

  // Extract individual pieces of state from storage
  const [tripData, setTripDataState] = useState<TripData | null>(tripStorage.tripData);
  const [transitRoutes, setTransitRoutes] = useState<{ [key: string]: TransitRouteData }>(tripStorage.transitRoutes);
  const [accommodationSearchResults, setAccommodationSearchResults] = useState<{ [cityName: string]: any[] }>(
    tripStorage.accommodationSearchResults || {}
  );
  const [transportConnectorData, setTransportConnectorData] = useState<{ [key: number]: TransportConnectorData }>(
    tripStorage.transportConnectorData
  );
  const [isFlightsLoading, setIsFlightsLoading] = useState(tripStorage.loadingStates.isFlightsLoading);
  const [hasInitialFlightSearchCompleted, setHasInitialFlightSearchCompleted] = useState(
    tripStorage.loadingStates.hasInitialFlightSearchCompleted
  );
  const [isAccommodationsLoading, setIsAccommodationsLoading] = useState(
    tripStorage.loadingStates.isAccommodationsLoading
  );
  const [hasInitialAccommodationSearchCompleted, setHasInitialAccommodationSearchCompleted] = useState(
    tripStorage.loadingStates.hasInitialAccommodationSearchCompleted
  );
  const [isFlightRefreshing, setIsFlightRefreshing] = useState(false); // New state for flight re-search
  
  // Sync state changes from other tabs
  useEffect(() => {
    console.log('[TripContext] Cross-tab storage sync detected');
    setTripDataState(tripStorage.tripData);
    setTransitRoutes(tripStorage.transitRoutes);
    setTransportConnectorData(tripStorage.transportConnectorData);
    setAccommodationSearchResults(tripStorage.accommodationSearchResults || {});
    setIsFlightsLoading(tripStorage.loadingStates.isFlightsLoading);
    setHasInitialFlightSearchCompleted(tripStorage.loadingStates.hasInitialFlightSearchCompleted);
    setIsAccommodationsLoading(tripStorage.loadingStates.isAccommodationsLoading);
    setHasInitialAccommodationSearchCompleted(tripStorage.loadingStates.hasInitialAccommodationSearchCompleted);
  }, [tripStorage]);

  // Add debounce timer for flight re-search
  const flightRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Function to refresh flights for new start/end cities
  const refreshFlightsAfterReorder = useCallback(async (cities: City[], tripDetails: any) => {
    if (!cities || cities.length < 2 || !tripDetails) return;
    
    const startCity = cities[0];
    const endCity = cities[cities.length - 1];
    
    console.log('🛩️ [TripContext] Refreshing flights after reorder:', {
      startCity: startCity.name,
      endCity: endCity.name,
      totalCities: cities.length
    });
    
    try {
      setIsFlightRefreshing(true);
      
      // Determine if this is a circular trip (start and end are same) or open-jaw
      const isCircularTrip = startCity.name === endCity.name;
      
      if (isCircularTrip) {
        // For circular trips, search for round-trip flights to the first city
        const flightData: FlightSearchResponse = await searchFlights({
          fromCity: 'London', // Assuming London as default departure
          toCity: startCity.name,
          departureDate: tripDetails.startDate,
          returnDate: tripDetails.endDate
        });
        
        // Convert FlightResult to FlightData format
        const outboundFlightData: FlightData | undefined = flightData.outbound ? {
          airline: flightData.outbound.airline || 'Unknown',
          flightTime: flightData.outbound.duration || 'Unknown',
          airport: 'LHR', // Default departure airport
          destination: startCity.name,
          price: flightData.outbound.price || 0,
          airlineLogo: flightData.outbound.airlineLogo,
          duration: flightData.outbound.duration,
          departureTime: flightData.outbound.departureTime,
          arrivalTime: flightData.outbound.arrivalTime,
          stops: flightData.outbound.stops,
          emissions: flightData.outbound.emissions,
          offer_id: flightData.outbound.offer_id
        } : undefined;
        
        const inboundFlightData: FlightData | undefined = flightData.inbound ? {
          airline: flightData.inbound.airline || 'Unknown',
          flightTime: flightData.inbound.duration || 'Unknown',
          airport: startCity.name,
          destination: 'London',
          price: flightData.inbound.price || 0,
          airlineLogo: flightData.inbound.airlineLogo,
          duration: flightData.inbound.duration,
          departureTime: flightData.inbound.departureTime,
          arrivalTime: flightData.inbound.arrivalTime,
          stops: flightData.inbound.stops,
          emissions: flightData.inbound.emissions,
          offer_id: flightData.inbound.offer_id
        } : undefined;
        
        // Update both outbound and inbound flights
        setTripDataState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            outboundFlight: outboundFlightData,
            inboundFlight: inboundFlightData
          };
        });
        
        console.log('✅ [TripContext] Updated circular trip flights:', {
          outbound: `London → ${startCity.name}`,
          inbound: `${endCity.name} → London`,
          outboundPrice: outboundFlightData?.price,
          inboundPrice: inboundFlightData?.price
        });
        
      } else {
        // For open-jaw trips, search for separate flights
        const [outboundData, inboundData]: [FlightSearchResponse, FlightSearchResponse] = await Promise.all([
          searchFlights({
            fromCity: 'London',
            toCity: startCity.name,
            departureDate: tripDetails.startDate
          }),
          searchFlights({
            fromCity: endCity.name,
            toCity: 'London',
            departureDate: tripDetails.endDate
          })
        ]);
        
        // Convert to FlightData format
        const outboundFlightData: FlightData | undefined = outboundData.outbound ? {
          airline: outboundData.outbound.airline || 'Unknown',
          flightTime: outboundData.outbound.duration || 'Unknown',
          airport: 'LHR',
          destination: startCity.name,
          price: outboundData.outbound.price || 0,
          airlineLogo: outboundData.outbound.airlineLogo,
          duration: outboundData.outbound.duration,
          departureTime: outboundData.outbound.departureTime,
          arrivalTime: outboundData.outbound.arrivalTime,
          stops: outboundData.outbound.stops,
          emissions: outboundData.outbound.emissions,
          offer_id: outboundData.outbound.offer_id
        } : undefined;
        
        const inboundFlightData: FlightData | undefined = inboundData.outbound ? {
          airline: inboundData.outbound.airline || 'Unknown',
          flightTime: inboundData.outbound.duration || 'Unknown',
          airport: endCity.name,
          destination: 'London',
          price: inboundData.outbound.price || 0,
          airlineLogo: inboundData.outbound.airlineLogo,
          duration: inboundData.outbound.duration,
          departureTime: inboundData.outbound.departureTime,
          arrivalTime: inboundData.outbound.arrivalTime,
          stops: inboundData.outbound.stops,
          emissions: inboundData.outbound.emissions,
          offer_id: inboundData.outbound.offer_id
        } : undefined;
        
        // Update flights
        setTripDataState(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            outboundFlight: outboundFlightData,
            inboundFlight: inboundFlightData
          };
        });
        
        console.log('✅ [TripContext] Updated open-jaw trip flights:', {
          outbound: `London → ${startCity.name}`,
          inbound: `${endCity.name} → London`,
          outboundPrice: outboundFlightData?.price,
          inboundPrice: inboundFlightData?.price
        });
      }
      
    } catch (error) {
      console.error('❌ [TripContext] Error refreshing flights after reorder:', error);
    } finally {
      setIsFlightRefreshing(false);
    }
  }, []);

  // Auto-save complete trip state to versioned localStorage whenever it changes
  const lastSaveTimeRef = useRef<number>(0);
  const SAVE_THROTTLE_MS = 500; // Increased throttle to 500ms for better performance
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save operation to prevent excessive writes
    saveTimeoutRef.current = setTimeout(() => {
      const now = Date.now();
      const timeSinceLastSave = now - lastSaveTimeRef.current;
      
      // Throttle saves to prevent excessive writes
      if (timeSinceLastSave < SAVE_THROTTLE_MS) {
        return;
      }
      
      lastSaveTimeRef.current = now;
      
      // Build complete storage object
      const updatedStorage: TripStorageSchemaV1 = {
        tripData,
        transitRoutes,
        transportConnectorData,
        accommodationSearchResults,
        loadingStates: {
          isFlightsLoading,
          hasInitialFlightSearchCompleted,
          isAccommodationsLoading,
          hasInitialAccommodationSearchCompleted,
        },
        lastSaved: now,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      };
      
      // Compress before saving to reduce storage size
      const compressed = compressTripStorage(updatedStorage);
      
      // Save using versioned storage hook
      setTripStorage(compressed);
      
      if (tripData) {
        console.log('💾 TripContext: Auto-saved trip data (versioned)', {
          cities: tripData.selectedCities?.map(c => c.name) || [],
          citiesCount: tripData.selectedCities?.length || 0,
          tripId: tripData.id,
          version: CURRENT_TRIP_STORAGE_VERSION,
          timestamp: new Date(now).toLocaleTimeString()
        });
      }
    }, 100); // 100ms debounce delay
    
    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    tripData, 
    transitRoutes, 
    transportConnectorData, 
    accommodationSearchResults,
    isFlightsLoading,
    hasInitialFlightSearchCompleted,
    isAccommodationsLoading,
    hasInitialAccommodationSearchCompleted,
    setTripStorage
  ]);

  // Calculate start date for a city at a specific index
  const calculateStartDate = useCallback((index: number, data?: TripData): Date | undefined => {
    const currentTripData = data || tripData;
    if (!currentTripData) return undefined;
    
    try {
      if (!currentTripData.tripDetails?.startDate) {
        return undefined;
      }

      if (index === 0) {
        const date = new Date(currentTripData.tripDetails.startDate);
        if (isNaN(date.getTime())) {
          return undefined;
        }
        return date;
      }

      const previousDate = calculateStartDate(index - 1, data);
      if (!previousDate) {
        return undefined;
      }

      if (currentTripData.daysArray[index - 1] === undefined) {
        return undefined;
      }

      return addDays(previousDate, currentTripData.daysArray[index - 1]);
    } catch (error) {
      return undefined;
    }
  }, [tripData]);

  // Helper function to calculate end date for a city
  const calculateEndDate = useCallback((cityIndex: number, data: TripData): Date | undefined => {
    if (!data || cityIndex < 0 || cityIndex >= data.selectedCities.length) {
      return undefined;
    }
    
    const startDate = calculateStartDate(cityIndex, data);
    if (!startDate) return undefined;
    
    const daysInCity = data.daysArray[cityIndex] || 1;
    return addDays(startDate, daysInCity);
  }, [calculateStartDate]);

  // Function to automatically select accommodation for a newly added city
  const autoSelectAccommodationForCity = useCallback(async (
    city: City, 
    cleanCityName: string, 
    currentTripData: TripData
  ) => {
    try {
      console.log(`🏨 Starting automatic accommodation selection for ${cleanCityName}`);
      
      // Extract accommodation preferences from trip data
      const preferences = AccommodationService.extractPreferencesFromTripData(currentTripData);
      
      console.log(`Extracted preferences for ${cleanCityName}:`, preferences);
      
      // Calculate check-in and check-out dates for this city
      const cityIndex = currentTripData.selectedCities.findIndex(c => c.name === city.name);
      const startDate = calculateStartDate(cityIndex, currentTripData);
      const endDate = calculateEndDate(cityIndex, currentTripData);
      
      if (!startDate || !endDate) {
        console.log(`⚠️ Could not calculate dates for ${cleanCityName}, skipping accommodation selection`);
        return;
      }
      
      // Search for accommodations
      const selectedAccommodation = await AccommodationService.searchAccommodationsForCity(
        city,
        startDate,
        endDate,
        preferences,
        2 // Default to 2 guests
      );
      
      if (selectedAccommodation) {
        // Convert DuffelSearchResult to SelectedAccommodation format
        const accommodationPrice = parseFloat(selectedAccommodation.cheapest_rate_total_amount || '0');
        const numberOfNights = Math.max(1, differenceInDays(endDate, startDate));
        const pricePerNight = accommodationPrice / numberOfNights;
        
        // Update trip data with the selected accommodation
        setTripDataState(prevState => {
          if (!prevState) return prevState;
          
          const updatedCosts = {
            ...prevState.cityCosts[cleanCityName],
            accommodation: accommodationPrice, // Store total accommodation cost for entire stay
            accommodationName: selectedAccommodation.accommodation?.name || selectedAccommodation.name || 'Selected Accommodation',
            total: (prevState.cityCosts[cleanCityName]?.activities || 0) + 
                   (prevState.cityCosts[cleanCityName]?.transport || 0) + 
                   accommodationPrice
          };
          
          // Create day details for all days in this city
          const dayDetails = [];
          for (let i = 0; i < numberOfNights; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            
            dayDetails.push({
              date: currentDate,
              accommodation: selectedAccommodation.accommodation?.name || selectedAccommodation.name || 'Selected Accommodation',
              accommodationPrice: pricePerNight, // Keep per-night price in day details for display
              accommodationDetails: {
                id: selectedAccommodation.id,
                address: selectedAccommodation.accommodation?.location?.address?.line_one,
                rating: selectedAccommodation.accommodation?.rating,
                photos: selectedAccommodation.accommodation?.photos?.map(photo => photo.url) || [],
                description: (selectedAccommodation.accommodation as any)?.description
              }
            });
          }
          
          const newTripData = {
            ...prevState,
            cityCosts: {
              ...prevState.cityCosts,
              [cleanCityName]: updatedCosts
            },
            cityDayDetails: {
              ...prevState.cityDayDetails,
              [cleanCityName]: dayDetails
            }
          };
          
          // Save to storage
          try {
            localStorage.setItem('unsavedTripData', JSON.stringify(newTripData));
            sessionStorage.setItem('tripData', JSON.stringify(newTripData));
            console.log(`✅ Auto-selected accommodation for ${cleanCityName}:`, 
              selectedAccommodation.accommodation?.name || selectedAccommodation.name);
          } catch (error) {
            console.error('Error saving accommodation selection to storage:', error);
          }
          
          return newTripData;
        });
      } else {
        console.log(`❌ No suitable accommodation found for ${cleanCityName} with preferences:`, preferences);
      }
    } catch (error) {
      console.error(`❌ Error in automatic accommodation selection for ${cleanCityName}:`, error);
    }
  }, [calculateStartDate, calculateEndDate]);

  // Function to automatically select accommodations for multiple cities in parallel
  const autoSelectAccommodationsInParallel = useCallback(async (
    cities: City[], 
    currentTripData: TripData
  ) => {
    try {
      setIsAccommodationsLoading(true);
      console.log(`🏨 Starting parallel accommodation search for ${cities.length} cities`);
      
      // Extract accommodation preferences from trip data
      const preferences = AccommodationService.extractPreferencesFromTripData(currentTripData);
      console.log(`Extracted preferences for parallel search:`, preferences);
      
      // Create parallel accommodation search promises
      const accommodationPromises = cities.map(async (city) => {
        const cleanCityName = city.name.split(',')[0].trim();
        const cityIndex = currentTripData.selectedCities.findIndex(c => c.name === city.name);
        const startDate = calculateStartDate(cityIndex, currentTripData);
        const endDate = calculateEndDate(cityIndex, currentTripData);
        
        if (!startDate || !endDate) {
          console.log(`⚠️ Could not calculate dates for ${cleanCityName}, skipping accommodation selection`);
          return { city, cleanCityName, accommodation: null };
        }
        
        try {
          let selectedAccommodation = await AccommodationService.searchAccommodationsForCity(
            city,
            startDate,
            endDate,
            preferences,
            2 // Default to 2 guests
          );
          
          // Enhanced fallback for failed accommodation searches (especially for Krabi)
          if (!selectedAccommodation && preferences.type === 'hostel') {
            console.log(`🔄 No hostels found for ${cleanCityName}, trying hotels as fallback...`);
            const hotelPreferences = { ...preferences, type: 'hotels' as const };
            selectedAccommodation = await AccommodationService.searchAccommodationsForCity(
              city,
              startDate,
              endDate,
              hotelPreferences,
              2
            );
          }
          
          return { 
            city, 
            cleanCityName, 
            accommodation: selectedAccommodation,
            startDate,
            endDate 
          };
        } catch (error) {
          console.error(`❌ Error searching accommodation for ${cleanCityName}:`, error);
          return { city, cleanCityName, accommodation: null };
        }
      });
      
      // Wait for all accommodation searches to complete
      const accommodationResults = await Promise.all(accommodationPromises);
      console.log(`✅ Parallel accommodation search completed for ${accommodationResults.length} cities`);
      
      // Count successful and failed searches
      const successfulSearches = accommodationResults.filter(result => result.accommodation !== null);
      const failedSearches = accommodationResults.filter(result => result.accommodation === null);
      
      console.log(`📊 Accommodation search results: ${successfulSearches.length} successful, ${failedSearches.length} failed`);
      if (failedSearches.length > 0) {
        console.log(`❌ Failed accommodation searches for:`, failedSearches.map(r => r.cleanCityName));
      }
      
      // Update trip data with all selected accommodations in a single state update
      setTripDataState(prevState => {
        if (!prevState) return prevState;
        
        let updatedTripData = { ...prevState };
        
        accommodationResults.forEach(({ city, cleanCityName, accommodation, startDate, endDate }) => {
          if (accommodation && startDate && endDate) {
            const accommodationPrice = parseFloat(accommodation.cheapest_rate_total_amount || '0');
            const numberOfNights = Math.max(1, differenceInDays(endDate, startDate));
            const pricePerNight = accommodationPrice / numberOfNights;
            
            // Update city costs
            const updatedCosts = {
              ...updatedTripData.cityCosts[cleanCityName],
              accommodation: accommodationPrice, // Store total accommodation cost for entire stay
              accommodationName: accommodation.accommodation?.name || accommodation.name || 'Selected Accommodation',
              total: (updatedTripData.cityCosts[cleanCityName]?.activities || 0) + 
                     (updatedTripData.cityCosts[cleanCityName]?.transport || 0) + 
                     accommodationPrice
            };
            
            // Create day details for all days in this city
            const dayDetails = [];
            for (let i = 0; i < numberOfNights; i++) {
              const currentDate = new Date(startDate);
              currentDate.setDate(startDate.getDate() + i);
              
              dayDetails.push({
                date: currentDate,
                accommodation: accommodation.accommodation?.name || accommodation.name || 'Selected Accommodation',
                accommodationPrice: pricePerNight, // Keep per-night price in day details for display
                accommodationDetails: {
                  id: accommodation.id,
                  address: accommodation.accommodation?.location?.address?.line_one,
                  rating: accommodation.accommodation?.rating,
                  photos: accommodation.accommodation?.photos?.map(photo => photo.url) || [],
                  description: (accommodation.accommodation as any)?.description
                }
              });
            }
            
            updatedTripData = {
              ...updatedTripData,
              cityCosts: {
                ...updatedTripData.cityCosts,
                [cleanCityName]: updatedCosts
              },
              cityDayDetails: {
                ...updatedTripData.cityDayDetails,
                [cleanCityName]: dayDetails
              }
            };
            
            console.log(`✅ Auto-selected accommodation for ${cleanCityName}:`, 
              accommodation.accommodation?.name || accommodation.name);
          } else {
            console.log(`❌ No suitable accommodation found for ${cleanCityName}`);
          }
        });
        
        // Save to storage
        try {
          localStorage.setItem('unsavedTripData', JSON.stringify(updatedTripData));
          sessionStorage.setItem('tripData', JSON.stringify(updatedTripData));
        } catch (error) {
          console.error('Error saving parallel accommodation selections to storage:', error);
        }
        
        return updatedTripData;
      });
      
    } catch (error) {
      console.error(`❌ Error in parallel accommodation selection:`, error);
    } finally {
      // Always set loading to false and completion to true, even if some searches failed
      // This prevents the app from getting stuck on "Preparing Your Trip"
      setIsAccommodationsLoading(false);
      setHasInitialAccommodationSearchCompleted(true);
      console.log(`🏁 Accommodation search process completed - loading: false, completed: true`);
    }
  }, [calculateStartDate, calculateEndDate, setIsAccommodationsLoading, setHasInitialAccommodationSearchCompleted]);

  // Wrapper for setTripData to ensure consistency when setting full trip data
  const setTripData = useCallback((data: TripData | null) => {
    console.log('🎯 TripContext: setTripData called with:', {
      hasData: !!data,
      cities: data?.selectedCities?.map(c => c.name) || [],
      citiesCount: data?.selectedCities?.length || 0,
      cityOrder: data?.selectedCities?.map((c, i) => `${i}: ${c.name}`) || [],
      tripId: data?.id || 'no-id',
      callStack: new Error().stack?.split('\n').slice(1, 4).join('\n')
    });
    
    // Before setting new trip data, ensure the data has the correct structure
    if (data) {
      // CRITICAL FIX: Normalize city IDs to prevent drag-and-drop issues
      const normalizedCities = data.selectedCities.map((city, index) => {
        let normalizedId = city.id;
        
        // Fix backend-generated unique-id patterns and ensure they're unique
        if (!normalizedId || normalizedId.startsWith('unique-id-')) {
          // Generate a proper nanoid for cities with problematic IDs
          normalizedId = nanoid();
          console.log(`🔧 Fixed problematic city ID: "${city.id}" -> "${normalizedId}" for city: ${city.name}`);
        }
        
        // Ensure the ID is a valid string and unique
        if (typeof normalizedId !== 'string' || normalizedId.length < 3) {
          normalizedId = nanoid();
          console.log(`🔧 Generated new ID for invalid ID: "${city.id}" -> "${normalizedId}" for city: ${city.name}`);
        }
        
        return {
          ...city,
          id: normalizedId
        };
      });
      
      // Validate that all city IDs are unique after normalization
      const cityIds = normalizedCities.map(c => c.id);
      const uniqueIds = new Set(cityIds);
      if (cityIds.length !== uniqueIds.size) {
        console.error('🚨 Duplicate city IDs detected after normalization, regenerating all IDs');
        // If we still have duplicates, regenerate all IDs
        normalizedCities.forEach((city, index) => {
          city.id = nanoid();
        });
      }
      
      // Make deep copy to avoid unintended reference issues
      const formattedData = {
        ...data,
        tripDetails: {
          ...data.tripDetails,
          // Convert string dates to Date objects if needed
          startDate: data.tripDetails.startDate instanceof Date 
            ? data.tripDetails.startDate 
            : new Date(data.tripDetails.startDate),
          endDate: data.tripDetails.endDate instanceof Date 
            ? data.tripDetails.endDate 
            : new Date(data.tripDetails.endDate),
          aiAssistData: {
            ...data.tripDetails.aiAssistData
          }
        },
        selectedCities: normalizedCities, // Use normalized cities with fixed IDs
        daysArray: [...data.daysArray],
        cityCosts: data.cityCosts && Object.keys(data.cityCosts).length > 0 
          ? { ...data.cityCosts } 
          : normalizedCities.reduce((acc, city) => {
              const cleanCityName = city.name.split(',')[0].trim();
              acc[cleanCityName] = {
                accommodation: 0,
                activities: 0,
                transport: 0,
                total: 0
              };
              return acc;
            }, {} as { [key: string]: CityCostDetails }),
        cityColors: updateCityColorsArray(normalizedCities.map(c => c.name)),
        cityDayDetails: { ...data.cityDayDetails },
        cityNotes: { ...data.cityNotes },
        totalTripCost: data.totalTripCost || 0,
        id: data.id || nanoid(),
        createdAt: data.createdAt || Date.now(),
        // Smart defaults for search flags: if trip has flight/accommodation data, assume search was enabled
        includeFlightSearch: data.includeFlightSearch !== undefined 
          ? data.includeFlightSearch 
          : !!(data.outboundFlight || data.inboundFlight),
        includeAccommodationSearch: data.includeAccommodationSearch !== undefined 
          ? data.includeAccommodationSearch 
          : !!(data.cityCosts && Object.keys(data.cityCosts).some(city => 
              data.cityCosts![city]?.accommodationName
            )), // Default to true if accommodation data exists
        // Preserve flight data when loading trips
        outboundFlight: data.outboundFlight ? { ...data.outboundFlight } : undefined,
        inboundFlight: data.inboundFlight ? { ...data.inboundFlight } : undefined
      };
      
      console.log('🎯 TripContext: Formatted data will be set with city order:', {
        formattedCities: formattedData.selectedCities.map(c => c.name),
        formattedCityOrder: formattedData.selectedCities.map((c, i) => `${i}: ${c.name}`),
        cityIdsFixed: formattedData.selectedCities.map(c => ({ name: c.name, id: c.id })),
        hasOutboundFlight: !!formattedData.outboundFlight,
        hasInboundFlight: !!formattedData.inboundFlight,
        outboundFlightPrice: formattedData.outboundFlight?.price,
        inboundFlightPrice: formattedData.inboundFlight?.price
      });
      
      setTripDataState(formattedData);
      
      // Only trigger parallel accommodation selection if user has enabled accommodation search
      // AND cities need accommodations AND accommodations are not already preloaded
      setTimeout(() => {
        if (formattedData.selectedCities && 
            formattedData.selectedCities.length > 0 && 
            formattedData.includeAccommodationSearch === true &&
            !(data as any).accommodationsPreloaded) { // Skip if accommodations are already loaded from AI Assist
          
          const citiesNeedingAccommodation = formattedData.selectedCities.filter((city, index) => {
            const cleanCityName = city.name.split(',')[0].trim();
            const hasCosts = formattedData.cityCosts?.[cleanCityName]?.accommodationName;
            const hasDayDetails = formattedData.cityDayDetails?.[cleanCityName]?.some(day => day.accommodation);
            
            return !hasCosts && !hasDayDetails;
          });
          
          if (citiesNeedingAccommodation.length > 0) {
            console.log(`🏨 User enabled accommodation search - starting parallel accommodation search for ${citiesNeedingAccommodation.length} cities`);
            autoSelectAccommodationsInParallel(citiesNeedingAccommodation, formattedData);
          }
        } else if (formattedData.includeAccommodationSearch === false) {
          console.log(`🚫 User disabled accommodation search - skipping auto-accommodation selection`);
        } else if ((data as any).accommodationsPreloaded) {
          console.log(`🏨 Accommodations already preloaded from AI Assist - skipping duplicate search`);
          // Mark accommodation search as completed since data is already loaded
          try {
            setHasInitialAccommodationSearchCompleted(true);
            setIsAccommodationsLoading(false);
          } catch (error) {
            console.error('Error setting accommodation completion flags:', error);
          }
        }
      }, 1000); // Delay to ensure UI and context are ready
    } else {
      setTripDataState(null);
    }
  }, [autoSelectAccommodationForCity]);

  const storeAccommodation = useCallback((cityName: string, accommodation: SelectedAccommodation) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      // Update city costs with accommodation details
      const prevCityCosts = prev.cityCosts[cityName] || {
        accommodation: 0,
        activities: 0,
        transport: 0,
        total: 0
      };
      
      const updatedCityCosts = {
        ...prevCityCosts,
        accommodation: accommodation.price,
        accommodationName: accommodation.name,
        roomName: accommodation.roomName,
        boardType: accommodation.boardType,
        rateId: accommodation.rateId,
        accommodationId: accommodation.id,
        accommodationAddress: accommodation.address,
        accommodationRating: accommodation.rating,
        isCustomAccommodation: accommodation.isCustom || false,
        total: (prevCityCosts.activities || 0) + 
               (prevCityCosts.transport || 0) + 
               accommodation.price
      };
      
      // Create a base day detail object with accommodation info
      const accommodationDetails = {
          roomName: accommodation.roomName,
          boardType: accommodation.boardType,
          rateId: accommodation.rateId,
          id: accommodation.id,
          address: accommodation.address,
          rating: accommodation.rating,
          description: accommodation.description,
          photos: accommodation.photos,
          amenities: accommodation.amenities
      };
      
      // Get existing day details for this city or initialize empty array
      let existingDayDetails = prev.cityDayDetails[cityName] || [];
      
      // Find the index of the city in selectedCities to calculate correct start date
      const cityIndex = prev.selectedCities.findIndex(city => city.name === cityName);
      
      if (cityIndex !== -1) {
        const cityStart = calculateStartDate(cityIndex, prev);
        if (cityStart) {
          const cityDays = prev.daysArray[cityIndex] || 1;
          
          // Create or update day details for each day in the city
          const updatedDayDetails = Array.from({ length: cityDays }, (_, i) => {
            const date = addDays(cityStart, i);
            const existingDay = existingDayDetails.find(d => 
              d.date instanceof Date && 
              d.date.toDateString() === date.toDateString()
            );
            
            return {
              ...existingDay,
              date,
              accommodation: accommodation.name,
              accommodationPrice: accommodation.price,
              accommodationDetails
            };
          });
          
          console.log(`Updated day details for ${cityName}:`, updatedDayDetails.map(d => ({
            date: d.date.toLocaleDateString(),
            accommodation: d.accommodation,
            price: d.accommodationPrice
          })));
          
          // Preserve the original trip ID when updating
          return {
            ...prev,
            id: prev.id, // Ensure we keep the original trip ID
            cityCosts: {
              ...prev.cityCosts,
              [cityName]: updatedCityCosts
            },
            cityDayDetails: {
              ...prev.cityDayDetails,
              [cityName]: updatedDayDetails
            }
          };
        }
      }
      
      // If city not found, just return the previous state
      return prev;
    });
  }, [calculateStartDate]);

  // Load trip function to fetch trip data with all routes
  const loadTrip = useCallback(async (userId: string, tripId: string) => {
    console.log('[TripContext] loadTrip called for', userId, tripId);
    try {
      // First load the main trip data
      const tripData = await tripApi.getTrip(userId, tripId);
      console.log('[TripContext] Trip data loaded:', tripData);
      
      // Check if it has route references
      if (tripData && tripData.transitRoutes) {
        const routeKeys = Object.keys(tripData.transitRoutes);
        const loadedRoutes: { [key: string]: TransitRouteData } = {};
        
        // Load each route from its separate document
        for (const routeKey of routeKeys) {
          try {
            console.log(`[TripContext] Loading route ${routeKey} from Firebase...`);
            const routeData = await tripApi.getTransitRoute(
              userId, 
              tripId,
              routeKey
            );
            
            if (routeData) {
              console.log(`[TripContext] Route ${routeKey} loaded successfully:`, routeData);
              loadedRoutes[routeKey] = routeData;
            }
          } catch (err) {
            console.error(`[TripContext] Error loading route ${routeKey}:`, err);
            continue;
          }
        }
        
        // Set the transit routes state
        setTransitRoutes(loadedRoutes);
        console.log('[TripContext] setTransitRoutes called from loadTrip:', loadedRoutes);
      } else {
        console.log('[TripContext] No transit routes found in trip data, keeping existing routes');
      }
      
      // Set the trip data state
      setTripData(tripData);
      console.log('[TripContext] setTripData called from loadTrip:', tripData);
      
      // For loaded trips, immediately set completion flags based on existing data
      if (tripData) {
        // Check if trip has flight data
        const hasFlightData = !!(tripData.outboundFlight || tripData.inboundFlight);
        if (hasFlightData || !tripData.includeFlightSearch) {
          setHasInitialFlightSearchCompleted(true);
          console.log('[TripContext] Set flight search completed for loaded trip');
        }
        
        // Check if trip has accommodation data
        const hasAccommodationData = tripData.selectedCities?.some(city => {
          const cleanCityName = city.name.split(',')[0].trim();
          const hasCosts = tripData.cityCosts?.[cleanCityName]?.accommodationName;
          const hasDayDetails = tripData.cityDayDetails?.[cleanCityName]?.some(day => day.accommodation);
          return hasCosts || hasDayDetails;
        });
        
        if (hasAccommodationData || !tripData.includeAccommodationSearch) {
          setHasInitialAccommodationSearchCompleted(true);
          console.log('[TripContext] Set accommodation search completed for loaded trip');
        }
      }
    } catch (error) {
      console.error('[TripContext] Error loading trip:', error);
      return;
    }
    console.log('[TripContext] loadTrip finished');
  }, [setTripData]);

  // Create a key for transit routes based on city names
  const createRouteKey = (fromCity: string, toCity: string): string => {
    return `${fromCity}-${toCity}`;
  };

  // Update transit route data
  const updateTransitRoute = useCallback((fromCity: string, toCity: string, routeData: TransitRouteData) => {
    console.log('[TripContext] updateTransitRoute called:', { fromCity, toCity, routeData });
    const routeKey = createRouteKey(fromCity, toCity);
    setTransitRoutes(prev => {
      const newRoutes = {
        ...prev,
        [routeKey]: {
          ...routeData,
          fromCity,
          toCity
        }
      };
      console.log('[TripContext] setTransitRoutes called from updateTransitRoute:', newRoutes);
      // Save to both localStorage and sessionStorage
      try {
        const savedData = localStorage.getItem('unsavedTripData');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          parsedData.transitRoutes = newRoutes;
          localStorage.setItem('unsavedTripData', JSON.stringify(parsedData));
          sessionStorage.setItem('tripData', JSON.stringify(parsedData));
        }
      } catch (error) {
        console.error('[TripContext] Error saving transit routes to storage:', error);
      }
      return newRoutes;
    });
  }, []);

  // Get transit route data
  const getTransitRoute = (fromCity: string, toCity: string): TransitRouteData | undefined => {
    // First try the direct route key format
    const routeKey = `${fromCity}-${toCity}`;
    
    // Log if the exact key is found
    if (transitRoutes[routeKey]) {
      return transitRoutes[routeKey];
    }
    
    // Try looking for partial matches (in case of case sensitivity issues)
    const matchingKey = Object.keys(transitRoutes).find(key => {
      return key.toLowerCase() === routeKey.toLowerCase();
    });
    
    if (matchingKey) {
      return transitRoutes[matchingKey];
    }
    
    // Try looking for a route with cities in reverse order
    const reverseRouteKey = `${toCity}-${fromCity}`;
    
    if (transitRoutes[reverseRouteKey]) {
      return transitRoutes[reverseRouteKey];
    }
    
    // Try looking for partial matches for reverse key
    const reverseMatchingKey = Object.keys(transitRoutes).find(key => {
      return key.toLowerCase() === reverseRouteKey.toLowerCase();
    });
    
    if (reverseMatchingKey) {
      return transitRoutes[reverseMatchingKey];
    }
    
    // No matches found
    return undefined;
  };

  // Clear all transit routes
  const clearTransitRoutes = useCallback(() => {
    setTransitRoutes({});
    console.log('[TripContext] clearTransitRoutes called, transitRoutes set to {}');
  }, []);

  // Transport connector data functions
  const updateTransportConnectorData = useCallback((index: number, data: TransportConnectorData) => {
    console.log('[TripContext] updateTransportConnectorData called:', { index, data });
    setTransportConnectorData(prev => {
      const newData = {
        ...prev,
        [index]: data
      };
      console.log('[TripContext] Transport connector data updated:', newData);
      
      // Save to localStorage
      try {
        const savedData = localStorage.getItem('unsavedTripData');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          parsedData.transportConnectorData = newData;
          localStorage.setItem('unsavedTripData', JSON.stringify(parsedData));
          sessionStorage.setItem('tripData', JSON.stringify(parsedData));
        }
      } catch (error) {
        console.error('[TripContext] Error saving transport connector data to storage:', error);
      }
      
      return newData;
    });
  }, []);

  const getTransportConnectorData = useCallback((index: number): TransportConnectorData | undefined => {
    return transportConnectorData[index];
  }, [transportConnectorData]);

  const clearTransportConnectorData = useCallback(() => {
    setTransportConnectorData({});
    console.log('[TripContext] clearTransportConnectorData called, transportConnectorData set to {}');
  }, []);

  // New methods for comprehensive trip management
  const updateTripDetails = useCallback((details: Partial<TripData['tripDetails']>) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        tripDetails: {
          ...prev.tripDetails,
          ...details
        }
      };
    });
  }, []);

  const updateSelectedCities = useCallback((cities: City[]) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        selectedCities: cities
      };
    });
  }, []);

  const addCity = useCallback((city: City) => {
    console.log('🏙️ [TripContext] addCity called with:', city.name);
    setTripDataState(prev => {
      if (!prev) {
        console.log('⚠️ [TripContext] addCity - no previous trip data');
        return prev;
      }
      
      console.log('✅ [TripContext] Previous trip data exists, adding city...');
      console.log('📊 [TripContext] Current cities before adding:', prev.selectedCities.map(c => c.name));
      
      // CRITICAL FIX: Ensure city has a proper, validated ID
      let validatedId = city.id;
      
      // Fix problematic IDs from backend or other sources
      if (!validatedId || validatedId.startsWith('unique-id-') || typeof validatedId !== 'string' || validatedId.length < 3) {
        validatedId = nanoid();
        console.log(`🔧 Fixed problematic city ID in addCity: "${city.id}" -> "${validatedId}" for city: ${city.name}`);
      }
      
      // Ensure the ID is unique among existing cities
      const existingIds = prev.selectedCities.map(c => c.id);
      if (existingIds.includes(validatedId)) {
        validatedId = nanoid();
        console.log(`🔧 Generated new unique ID to avoid conflict: "${city.id}" -> "${validatedId}" for city: ${city.name}`);
      }
      
      const cityWithId = {
        ...city,
        id: validatedId
      };
      
      // Clean the city name to ensure no duplicated information (like "Koh TaoKo Tao")
      // Take only the first part of the name before any comma or duplicate location part
      const cleanCityName = cityWithId.name.split(',')[0].trim();
      
      // Generate a distinct pastel color for the city
      const allCityNames = [...prev.selectedCities, cityWithId].map(c => c.name.split(',')[0].trim());
      const newColor = generateDistinctPastelColor(cleanCityName, allCityNames);
      
      const updatedTripData = {
        ...prev,
        selectedCities: [...prev.selectedCities, cityWithId],
        daysArray: [...prev.daysArray, 1], // Default to 1 day
        cityColors: [...prev.cityColors, { city: cleanCityName, color: newColor }],
        cityCosts: {
          ...prev.cityCosts,
          [cleanCityName]: {
            accommodation: 0,
            activities: 0,
            transport: 0,
            total: 0
          }
        },
        cityDayDetails: {
          ...prev.cityDayDetails,
          [cleanCityName]: []
        }
      };
      
      console.log('✅ [TripContext] Updated trip data with new city:', {
        cityName: cityWithId.name,
        totalCities: updatedTripData.selectedCities.length,
        allCities: updatedTripData.selectedCities.map(c => c.name)
      });
      
      // Optimized storage: Use async storage operations to prevent blocking
      try {
        // Use requestIdleCallback for non-critical storage operations
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            localStorage.setItem('unsavedTripData', JSON.stringify(updatedTripData));
            sessionStorage.setItem('tripData', JSON.stringify(updatedTripData));
          });
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            localStorage.setItem('unsavedTripData', JSON.stringify(updatedTripData));
            sessionStorage.setItem('tripData', JSON.stringify(updatedTripData));
          }, 0);
        }
        
        console.log('✅ [TripContext] City added and queued for storage:', {
          cityName: cityWithId.name,
          totalCities: updatedTripData.selectedCities.length,
          allCities: updatedTripData.selectedCities.map(c => c.name)
        });
      } catch (error) {
        console.error('❌ [TripContext] Error saving city to storage:', error);
      }
      
      // Only trigger automatic accommodation selection if accommodation search is enabled
      // This should only happen for AI Assist trips, not manual city additions
      if (updatedTripData.includeAccommodationSearch === true) {
        console.log('🏨 [TripContext] Accommodation search enabled - triggering auto-selection for manually added city');
        // Use requestIdleCallback to defer accommodation search until browser is idle
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            autoSelectAccommodationsInParallel([cityWithId], updatedTripData);
          }, { timeout: 2000 });
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            autoSelectAccommodationsInParallel([cityWithId], updatedTripData);
          }, 200);
        }
      } else {
        console.log('🚫 [TripContext] Accommodation search disabled - skipping auto-selection for manually added city');
      }
      
      return updatedTripData;
    });
  }, [autoSelectAccommodationsInParallel]);

  const storeAccommodationSearchResults = useCallback((cityName: string, results: any[]) => {
    const cleanCityName = cityName.split(',')[0].trim();
    setAccommodationSearchResults(prev => ({
      ...prev,
      [cleanCityName]: results
    }));
  }, []);

  const getAccommodationSearchResults = useCallback((cityName: string) => {
    const cleanCityName = cityName.split(',')[0].trim();
    return accommodationSearchResults[cleanCityName];
  }, [accommodationSearchResults]);

  const resetTripContext = useCallback(() => {
    setTripDataState(null);
    clearTransitRoutes();
    clearTransportConnectorData();
    setAccommodationSearchResults({});
    setIsFlightsLoading(false);
    setHasInitialFlightSearchCompleted(false);
    setIsAccommodationsLoading(false);
    setHasInitialAccommodationSearchCompleted(false);
  }, [clearTransitRoutes, clearTransportConnectorData]);

  const removeCity = useCallback((cityName: string) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      const cityIndex = prev.selectedCities.findIndex(c => c.name === cityName);
      if (cityIndex === -1) return prev;
      
      // Clean the city name for consistent handling
      const cleanCityName = cityName.split(',')[0].trim();
      
      // Remove from color cache
      removeColorForCity(cleanCityName);
      
      // Create new arrays without the city
      const newSelectedCities = [...prev.selectedCities];
      newSelectedCities.splice(cityIndex, 1);
      
      const newDaysArray = [...prev.daysArray];
      newDaysArray.splice(cityIndex, 1);
      
      // Remove from city colors
      const newCityColors = prev.cityColors.filter(cc => cc.city !== cleanCityName);
      
      // Remove from costs and day details
      const newCityCosts = { ...prev.cityCosts };
      delete newCityCosts[cleanCityName];
      
      const newCityDayDetails = { ...prev.cityDayDetails };
      delete newCityDayDetails[cleanCityName];
      
      const updatedTripData = {
        ...prev,
        selectedCities: newSelectedCities,
        daysArray: newDaysArray,
        cityColors: newCityColors,
        cityCosts: newCityCosts,
        cityDayDetails: newCityDayDetails
      };
      
      // Save to both localStorage and sessionStorage immediately
      try {
        localStorage.setItem('unsavedTripData', JSON.stringify(updatedTripData));
        sessionStorage.setItem('tripData', JSON.stringify(updatedTripData));
        console.log('TripContext: City removed and saved to storage:', cityName);
      } catch (error) {
        console.error('Error saving after city removal to storage:', error);
      }
      
      return updatedTripData;
    });
  }, []);

  const updateDaysArray = useCallback((daysArray: number[]) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        daysArray
      };
    });
  }, []);

  const updateDaysForCity = useCallback((index: number, days: number) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      const newDaysArray = [...prev.daysArray];
      newDaysArray[index] = days;
      
      return {
        ...prev,
        daysArray: newDaysArray
      };
    });
  }, []);

  const updateCityCosts = useCallback((cityCosts: { [key: string]: CityCostDetails }) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        cityCosts
      };
    });
  }, []);

  const updateCityCost = useCallback((
    cityName: string,
    costType: keyof CityCostDetails,
    value: number | string
  ) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      const cityPrevCosts = prev.cityCosts[cityName] || {
        accommodation: 0,
        activities: 0,
        transport: 0,
        total: 0
      };
      
      const updatedCityCosts = { ...cityPrevCosts, [costType]: value };
      
      // Recalculate total if we're updating a numeric field
      if (
        costType === 'accommodation' || 
        costType === 'activities' || 
        costType === 'transport'
      ) {
        updatedCityCosts.total =
          (updatedCityCosts.accommodation || 0) +
          (updatedCityCosts.activities || 0) +
          (updatedCityCosts.transport || 0);
      }
      
      return {
        ...prev,
        cityCosts: {
          ...prev.cityCosts,
          [cityName]: updatedCityCosts
        }
      };
    });
  }, []);

  const updateCityColors = useCallback((cityColors: Array<{ city: string; color: string }>) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        cityColors
      };
    });
  }, []);

  const updateCityDayDetails = useCallback((cityDayDetails: { [key: string]: DayDetails[] }) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        cityDayDetails
      };
    });
  }, []);

  const updateDayDetailsForCity = useCallback((cityName: string, dayDetails: DayDetails[]) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      // Only update if the day details have actually changed
      const existingDetails = prev.cityDayDetails[cityName];
      if (existingDetails && JSON.stringify(existingDetails) === JSON.stringify(dayDetails)) {
        return prev; // No change, return same state to prevent unnecessary re-renders
      }
      
      return {
        ...prev,
        cityDayDetails: {
          ...prev.cityDayDetails,
          [cityName]: dayDetails
        }
      };
    });
  }, []);

  const updateCityNotes = useCallback((cityId: string, notes: string) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        cityNotes: {
          ...prev.cityNotes,
          [cityId]: notes
        }
      };
    });
  }, []);

  const setOutboundFlight = useCallback((flight: FlightData | undefined) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        outboundFlight: flight
      };
    });
  }, []);

  const setInboundFlight = useCallback((flight: FlightData | undefined) => {
    setTripDataState(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        inboundFlight: flight
      };
    });
  }, []);
  
  const calculateTotalTripCost = useCallback((): number => {
    if (!tripData) return 0;
    
    const cityCostsTotal = Object.values(tripData.cityCosts).reduce(
      (total, cityData) => {
        const accommodationCost = Number(cityData.accommodation) || 0;
        const activitiesCost = Number(cityData.activities) || 0;
        const transportCost = Number(cityData.transport) || 0;
        return total + accommodationCost + activitiesCost + transportCost;
      },
      0
    );
    
    const outboundFlightCost = tripData.outboundFlight?.price || 0;
    const inboundFlightCost = tripData.inboundFlight?.price || 0;
    
    // Calculate transit route costs if applicable
    const transitCosts = Object.values(transitRoutes).reduce((total, route) => {
      return total + (route.price || 0);
    }, 0);
    
    // Calculate transport connector costs (intercity transport between cities)
    const transportConnectorCosts = Object.values(transportConnectorData).reduce((total, connectorData) => {
      return total + (Number(connectorData.cost) || 0);
    }, 0);
    
    console.log('💰 Cost calculation breakdown:', {
      cityCostsTotal,
      outboundFlightCost,
      inboundFlightCost,
      transitCosts,
      transportConnectorCosts,
      transportConnectorData
    });
    
    // Return the calculated total without updating state to prevent infinite loops
    const newTotalCost = cityCostsTotal + outboundFlightCost + inboundFlightCost + transitCosts + transportConnectorCosts;
    return newTotalCost;
  }, [tripData, transitRoutes, transportConnectorData]);
    
  // useEffect to update total trip cost when transit routes change
  useEffect(() => {
    if (tripData) {
      const newTotal = calculateTotalTripCost();
      console.log('🔄 TripContext: Recalculating total cost:', {
        currentTotal: tripData.totalTripCost,
        newTotal,
        cityCosts: tripData.cityCosts,
        outboundFlight: tripData.outboundFlight?.price,
        inboundFlight: tripData.inboundFlight?.price
      });
      
      if (Math.abs(tripData.totalTripCost - newTotal) > 0.01) { // Use small threshold for floating point comparison
        console.log('💰 TripContext: Updating total cost from', tripData.totalTripCost, 'to', newTotal);
        setTripDataState(prev => {
          if (!prev) return prev;
          
          // Prevent infinite loops by checking if the total is already correct
          if (Math.abs(prev.totalTripCost - newTotal) <= 0.01) {
            return prev; // No change needed
          }
          
          const updatedData = {
            ...prev,
            totalTripCost: newTotal
          };
          
          // Save to storage
          try {
            localStorage.setItem('unsavedTripData', JSON.stringify(updatedData));
            sessionStorage.setItem('tripData', JSON.stringify(updatedData));
          } catch (error) {
            console.error('Error saving updated total cost to storage:', error);
          }
          
          return updatedData;
        });
      }
    }
  }, [tripData?.cityCosts, tripData?.outboundFlight, tripData?.inboundFlight, transitRoutes, transportConnectorData, calculateTotalTripCost]);
  
  // Reorder cities (for drag and drop functionality)
  const reorderCities = useCallback((sourceIndex: number, destinationIndex: number) => {
    console.log('🔄 TripContext: reorderCities called:', {
      sourceIndex,
      destinationIndex,
      beforeCities: tripData?.selectedCities?.map(c => c.name) || [],
      beforeCityOrder: tripData?.selectedCities?.map((c, i) => `${i}: ${c.name}`) || []
    });
    
    setTripDataState(prev => {
      if (!prev) return prev;
      
      console.log('🔄 TripContext: Starting reorder operation with prev data:', {
        prevCities: prev.selectedCities.map(c => c.name),
        prevCityOrder: prev.selectedCities.map((c, i) => `${i}: ${c.name}`)
      });
      
      // Create new arrays with the reordered items
      const reorderedCities = [...prev.selectedCities];
      const [removedCity] = reorderedCities.splice(sourceIndex, 1);
      reorderedCities.splice(destinationIndex, 0, removedCity);
      
      const reorderedDays = [...prev.daysArray];
      const [removedDays] = reorderedDays.splice(sourceIndex, 1);
      reorderedDays.splice(destinationIndex, 0, removedDays);
      
      // Keep the original color assignments for each city
      const reorderedColors = prev.cityColors.slice(); // Keep colors as is, don't reorder them
      
      const updatedTripData = {
        ...prev,
        selectedCities: reorderedCities,
        daysArray: reorderedDays,
        cityColors: reorderedColors // Use original color assignments
      };
      
      console.log('✅ TripContext: Cities reordered successfully:', {
        afterCities: reorderedCities.map(c => c.name),
        afterCityOrder: reorderedCities.map((c, i) => `${i}: ${c.name}`),
        movedCity: removedCity.name,
        from: sourceIndex,
        to: destinationIndex,
        cityColors: reorderedColors
      });
      
      // Save to both localStorage and sessionStorage immediately to ensure persistence
      try {
        const timestamp = Date.now();
        localStorage.setItem('unsavedTripData', JSON.stringify(updatedTripData));
        sessionStorage.setItem('tripData', JSON.stringify(updatedTripData));
        
        console.log('💾 TripContext: Reordered data saved to localStorage immediately:', {
          savedCities: reorderedCities.map(c => c.name),
          savedCityOrder: reorderedCities.map((c, i) => `${i}: ${c.name}`),
          timestamp,
          timestampDate: new Date(timestamp).toLocaleString()
        });
        
        // Verify the data was actually saved
        const verificationData = JSON.parse(localStorage.getItem('unsavedTripData') || '{}');
        console.log('🔍 TripContext: Verification - data actually saved as:', {
          verifiedCities: verificationData.selectedCities?.map((c: any) => c.name) || [],
          verifiedCityOrder: verificationData.selectedCities?.map((c: any, i: number) => `${i}: ${c.name}`) || []
        });
        
      } catch (error) {
        console.error('Error saving city reordering to storage:', error);
      }
      
      return updatedTripData;
    });
  }, [tripData]);
  
  // Clear all storage and start fresh - useful when generating new trips
  const clearStorageAndStartFresh = useCallback(() => {
    console.log('🧹 TripContext: Clearing all storage and starting fresh');
    
    // Remove versioned storage using our hook
    removeTripStorage();
    
    // Clear legacy localStorage keys for backwards compatibility
    if (typeof window !== 'undefined') {
      localStorage.removeItem('unsavedTripData');
      sessionStorage.removeItem('tripData');
      sessionStorage.removeItem('aiAssistState');
    }
    
    // Reset trip context state
    setTripDataState(null);
    setTransitRoutes({});
    setAccommodationSearchResults({});
    setTransportConnectorData({});
    setIsFlightsLoading(false);
    setHasInitialFlightSearchCompleted(false);
    setIsAccommodationsLoading(false);
    setHasInitialAccommodationSearchCompleted(false);
    
    console.log('✅ TripContext: Storage cleared and context reset');
  }, [removeTripStorage]);
  
  // Create the context value object with all our functions
  const value: TripContextState = {
    tripData,
    transitRoutes,
    accommodationSearchResults,
    transportConnectorData,
    setTripData,
    updateTransitRoute,
    getTransitRoute,
    clearTransitRoutes,
    storeAccommodationSearchResults,
    getAccommodationSearchResults,
    updateTransportConnectorData,
    getTransportConnectorData,
    clearTransportConnectorData,
    loadTrip, // Add the new loadTrip function
    isFlightsLoading,
    setIsFlightsLoading,
    hasInitialFlightSearchCompleted,
    setHasInitialFlightSearchCompleted,
    isAccommodationsLoading,
    setIsAccommodationsLoading,
    hasInitialAccommodationSearchCompleted,
    setHasInitialAccommodationSearchCompleted,
    updateTripDetails,
    updateSelectedCities,
    addCity,
    removeCity,
    updateDaysArray,
    updateDaysForCity,
    updateCityCosts,
    updateCityCost,
    updateCityColors,
    updateCityDayDetails,
    updateDayDetailsForCity,
    updateCityNotes,
    setOutboundFlight,
    setInboundFlight,
    calculateTotalTripCost,
    calculateStartDate,
    reorderCities,
    resetTripContext,
    storeAccommodation,
    clearStorageAndStartFresh,
    isFlightRefreshing,
    setIsFlightRefreshing
  };

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
};

// Custom hook for using this context
export const useTripContext = () => {
  const context = useContext(TripContext);
  
  if (context === undefined) {
    throw new Error('useTripContext must be used within a TripProvider');
  }
  
  return context;
};

// Event bus for cross-component communication
export const TripEventBus = {
  events: {} as Record<string, Function[]>,
  
  // Subscribe to an event
  on(event: string, callback: Function) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  },
  
  // Unsubscribe from an event
  off(event: string, callback: Function) {
    if (this.events[event]) {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    }
  },
  
  // Emit an event with data
  emit(event: string, data?: any) {
    if (this.events[event]) {
      this.events[event].forEach(callback => callback(data));
    }
  }
};

// Export event constants
export const TRIP_EVENTS = {
  CITY_HOVER_START: 'city_hover_start',
  CITY_HOVER_END: 'city_hover_end'
};